import {
  CalendarMonthOutlined,
  ComputerOutlined,
  FlightTakeoffOutlined,
  LuggageOutlined,
  TranslateOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import { Box, Chip, Grid, Stack, Typography } from "@mui/joy";
import { ReactNode } from "react";
import { WORKING_DAYS } from "../constants";
import { Agent, DESTINATION_FIELDS, EXPERTISE_LEVELS, PRODUCT_FIELDS, getCertificationNames } from "../profileFields";
import { getBookingSystemsFullNames, getLanguagesFullNames } from "../utils";
import ExpertiseMeter from "./ExpertiseMeter";
import SectionCard from "./SectionCard";

type ExpertiseTabProps = {
  agent: Agent;
};

function ChipList({ items, emptyText = "None added" }: { items: ReactNode[]; emptyText?: string }) {
  if (!items.length) {
    return (
      <Typography level="body-sm" textColor="text.tertiary">
        {emptyText}
      </Typography>
    );
  }

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {items.map((item, index) => (
        <Chip key={index} variant="soft" color="neutral">
          {item}
        </Chip>
      ))}
    </Stack>
  );
}

function LevelLegend() {
  return (
    <Stack direction="row" gap={1.5} flexWrap="wrap">
      {EXPERTISE_LEVELS.map((level, index) => (
        <Stack key={level} direction="row" alignItems="center" gap={0.5}>
          <Stack direction="row" gap={0.25}>
            {EXPERTISE_LEVELS.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "1px",
                  backgroundColor: i <= index ? "primary.500" : "neutral.200",
                }}
              />
            ))}
          </Stack>
          <Typography level="body-xs" textColor="text.tertiary">
            {level}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function ExpertiseTab({ agent }: ExpertiseTabProps) {
  const workingDays = agent?.workingDays || {};
  const certifications = getCertificationNames(agent);

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5} columns={12}>
        <Grid xs={12} md={6}>
          <SectionCard title="Destinations" icon={<FlightTakeoffOutlined />} sx={{ height: "100%" }}>
            <Box>
              {DESTINATION_FIELDS.map(({ label, key }) => (
                <ExpertiseMeter key={key} label={label} value={agent?.destination?.[key]} />
              ))}
            </Box>
          </SectionCard>
        </Grid>
        <Grid xs={12} md={6}>
          <SectionCard title="Products & Travellers" icon={<LuggageOutlined />} sx={{ height: "100%" }}>
            <Box>
              {PRODUCT_FIELDS.map(({ label, key }) => (
                <ExpertiseMeter key={key} label={label} value={agent?.expertise?.[key]} />
              ))}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
      <LevelLegend />

      <Grid container spacing={2.5} columns={12}>
        <Grid xs={12} md={6}>
          <SectionCard title="Working Days" icon={<CalendarMonthOutlined />} sx={{ height: "100%" }}>
            {/* every day shown so gaps in availability are obvious at a glance */}
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {Object.entries(WORKING_DAYS).map(([key, label]) => {
                const works = workingDays[key] === true;
                return (
                  <Chip
                    key={key}
                    variant={works ? "solid" : "outlined"}
                    color={works ? "primary" : "neutral"}
                    sx={{ opacity: works ? 1 : 0.6 }}
                  >
                    {key === "holidays" ? label : label.slice(0, 3)}
                  </Chip>
                );
              })}
            </Stack>
          </SectionCard>
        </Grid>
        <Grid xs={12} md={6}>
          <SectionCard title="Languages Spoken" icon={<TranslateOutlined />} sx={{ height: "100%" }}>
            <ChipList items={getLanguagesFullNames(agent?.languageExpertise)} />
          </SectionCard>
        </Grid>
        <Grid xs={12} md={6}>
          <SectionCard title="Booking System Expertise" icon={<ComputerOutlined />} sx={{ height: "100%" }}>
            <ChipList items={getBookingSystemsFullNames(agent?.bookingSystemExpertise)} />
          </SectionCard>
        </Grid>
        <Grid xs={12} md={6}>
          <SectionCard title="Certifications" icon={<WorkspacePremiumOutlined />} sx={{ height: "100%" }}>
            {certifications.length ? (
              <Stack gap={1}>
                {certifications.map((certification) => (
                  <Stack key={certification} direction="row" alignItems="flex-start" gap={1}>
                    <WorkspacePremiumOutlined sx={{ color: "warning.500", mt: 0.25 }} fontSize="md" />
                    <Typography level="body-sm" textColor="text.primary">
                      {certification}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography level="body-sm" textColor="text.tertiary">
                No certifications
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Stack>
  );
}

export default ExpertiseTab;
